import React, { useState } from 'react';
import { Languages, Loader2, Wand2 } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { i18nAPI } from '../services/api';

export const TARGET_LANGS = [
  { code: 'en', label: 'English', flag: 'gb' },
  { code: 'de', label: 'Deutsch', flag: 'de' },
  { code: 'es', label: 'Español', flag: 'es' },
  { code: 'it', label: 'Italiano', flag: 'it' },
];

// Generic 4-language editor: fields = [{ key, label, source (FR text) }], values = { lang: { key: text } }
const TranslationsEditor = ({ fields, values = {}, onChange, context = '', testId = 'translations', title = 'Traductions (menu multilingue)' }) => {
  const [tab, setTab] = useState('en');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const filled = TARGET_LANGS.filter(l => fields.some(f => (values[l.code] || {})[f.key])).length;
  const sources = fields.filter(f => (f.source || '').trim());

  const setValue = (lang, key, text) => onChange({ ...values, [lang]: { ...(values[lang] || {}), [key]: text } });

  const autoTranslate = async () => {
    if (!sources.length) return;
    setBusy(true); setError(null);
    try {
      const { translations } = await i18nAPI.translate(Object.fromEntries(sources.map(f => [f.key, f.source])), context);
      const next = { ...values };
      TARGET_LANGS.forEach(l => { next[l.code] = { ...(values[l.code] || {}), ...(translations[l.code] || {}) }; });
      onChange(next);
    } catch (e) {
      setError(e.response?.data?.detail || 'Traduction impossible');
    } finally { setBusy(false); }
  };

  return (
    <div className="border border-gray-200 rounded-lg" data-testid={testId}>
      <div className="px-3 py-2 bg-gray-50 rounded-t-lg flex flex-wrap items-center justify-between gap-2">
        <p className="text-xs font-medium text-gray-600 flex items-center gap-1.5"><Languages size={13} className="text-orange-500" /> {title} <span className="font-normal text-gray-400">({filled}/4 langues renseignées)</span></p>
        <Button type="button" size="sm" variant="outline" onClick={autoTranslate} disabled={busy || !sources.length} className="h-7 text-xs border-orange-400 text-orange-700 hover:bg-orange-50" data-testid={`${testId}-auto`}>
          {busy ? <Loader2 size={13} className="mr-1 animate-spin" /> : <Wand2 size={13} className="mr-1" />} Traduire automatiquement (IA)
        </Button>
      </div>
      <div className="flex flex-wrap border-b" role="tablist">
        {TARGET_LANGS.map(l => {
          const done = fields.filter(f => (values[l.code] || {})[f.key]).length;
          return (
            <button key={l.code} type="button" role="tab" onClick={() => setTab(l.code)} data-testid={`${testId}-tab-${l.code}`}
              className={`flex items-center gap-1.5 px-3 py-1.5 text-xs border-b-2 -mb-px transition-colors ${tab === l.code ? 'border-orange-500 text-orange-700 font-semibold' : 'border-transparent text-gray-500 hover:text-gray-800'}`}>
              <img src={`https://flagcdn.com/w20/${l.flag}.png`} alt="" className="w-4 h-3 rounded-sm no-placeholder" /> {l.label}
              <span className={`text-[10px] ${done === fields.length && fields.length ? 'text-green-600' : 'text-gray-400'}`}>{done}/{fields.length}</span>
            </button>
          );
        })}
      </div>
      {error && <p className="px-3 pt-2 text-xs text-red-600" data-testid={`${testId}-error`}>{error}</p>}
      <div className="p-3 space-y-2 max-h-96 overflow-y-auto">
        {fields.length === 0 && <p className="text-xs text-gray-400 italic">Rien à traduire pour le moment.</p>}
        {fields.map(f => (
          <div key={f.key} className={`grid grid-cols-1 sm:grid-cols-[minmax(0,11rem)_1fr] gap-1 sm:gap-3 ${f.multiline ? '' : 'sm:items-center'}`}>
            <span className={`text-xs text-gray-600 ${f.multiline ? 'sm:self-start sm:pt-2 line-clamp-3' : 'truncate'}`} title={f.source}>{f.label} <span className="text-gray-400">· {f.source || '—'}</span></span>
            {f.multiline
              ? <Textarea value={(values[tab] || {})[f.key] || ''} onChange={(e) => setValue(tab, f.key, e.target.value)} placeholder={f.source || ''} maxLength={f.maxLength || 6000} rows={4} className="text-sm" data-testid={`${testId}-${tab}-${f.key}`} />
              : <Input value={(values[tab] || {})[f.key] || ''} onChange={(e) => setValue(tab, f.key, e.target.value)} placeholder={f.source || ''} maxLength={f.maxLength || 120} className="h-8 text-sm" data-testid={`${testId}-${tab}-${f.key}`} />}
          </div>
        ))}
      </div>
    </div>
  );
};

export default TranslationsEditor;
